import { ALO_API_HOST, ALO_OMS_HOST } from 'src/scripts/utils/constants';
import { BopisAnalyticsVariantType, BopisProductStoreAvailability, BopisProductsStoreAvailability } from './bopisTypes';
import { CartData } from 'src/scripts/utils/cart';
import { IStoreAvailabilityData, StoreLocatorResponseError } from '../StoreLocator/storeLocatorTypes';
import { bopisState, setIsFetchingData } from './bopisState';
import { customerLocationState } from '../StoreLocator/customerLocationState';
import { isEmpty } from 'lodash-es';
import { SiteSettings } from 'src/scripts/utils/environment';
import { getDestinationObject } from '../../utils';
import { IColorwayProductData, IVariant } from '../../types/PDP';
import { toJS } from 'mobx';

export const checkBopisAvailabilityByProducts = async (
  products: BopisProductStoreAvailability[],
  {
    shouldUseLocationId = false,
    includeInStoreData = false,
  }: {
    shouldUseLocationId?: boolean;
    includeInStoreData?: boolean;
  }
) => {
  const filteredProducts = products.filter((item) => item.quantity > 0); // [REVISIT] Once OMS team fixed the handling of out of stock products. Or once item with "0" qty is removed from the cart.

  const destination = getDestinationObject(customerLocationState.coordinates, customerLocationState.zip);
  if (
    !(destination?.latitude && destination?.longitude) &&
    !destination?.postal_code &&
    isEmpty(bopisState.storeSelection)
  )
    return { detail: 'Location is unknown' };
  setIsFetchingData(true);

  const requestBody = {
    rate: {
      destination: shouldUseLocationId ? { location_id: bopisState?.storeSelection?.[0]?.location_id } : destination, // Fetch based on user selected store if isCart === true;
      items: filteredProducts,
      in_store: includeInStoreData,
    },
  };

  const response = await fetch(`${ALO_API_HOST}/v1/product_availability`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    console.error(`HTTP error! status: ${response.status}`);
    setIsFetchingData(false);
    return { detail: response.status.toString() };
  }
  const data: BopisProductsStoreAvailability | StoreLocatorResponseError = await response.json();
  setIsFetchingData(false);
  return data;
};

export const checkBopisAvailabilityByCartForSidebar = async (items: BopisProductStoreAvailability[]) => {
  const destination = getDestinationObject(customerLocationState.coordinates, customerLocationState.zip);
  if (!(destination?.latitude && destination?.longitude) && !destination?.postal_code)
    return { detail: 'Location is unknown' };
  const filteredItems = items.filter((item) => item.quantity > 0); // [REVISIT] Once OMS team fixed the handling of out of stock products. Or once item with "0" qty is removed from the cart.
  setIsFetchingData(true);
  const requestBody = {
    rate: {
      destination,
      items: filteredItems,
    },
  };
  const response = await fetch(`${ALO_OMS_HOST}/ias/api/v1/find-availability/pickup?source=cart_availability`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    console.error(`HTTP error! status: ${response.status}`);
    setIsFetchingData(false);
    return { detail: response.status.toString() };
  }
  const data: IStoreAvailabilityData[] | StoreLocatorResponseError = await response.json();
  setIsFetchingData(false);
  return data;
};

export const checkInStoreDataAvailableInBopis = (productAvailabilityData: BopisProductsStoreAvailability | null) => {
  const inStoreFulfillmentNode = productAvailabilityData?.availabilityByProducts[0]?.availabilityByFulfillmentTypes.find(
    (type) => type.fulfillmentType.toLowerCase() === 'instore'
  );
  return !!inStoreFulfillmentNode;
};

export const extractStoresDataFromBopisProductAvailabilityData = (
  productAvailabilityData: BopisProductsStoreAvailability | null,
  fulfillmentType: 'pickup' | 'instore' = 'pickup'
) => {
  let stores: IStoreAvailabilityData[] = [];
  if (productAvailabilityData && 'availabilityByProducts' in productAvailabilityData) {
    const pickupFulfillmentNode = productAvailabilityData.availabilityByProducts[0]?.availabilityByFulfillmentTypes.find(
      (type) => type.fulfillmentType.toLowerCase() === fulfillmentType
    );
    stores = pickupFulfillmentNode?.availabilityDetails[0]?.availabilityByLocations || [];
  }
  return stores;
};

export const checkProductAvailability = (sku: string) => {
  const product = bopisState.cartProductsAvailability?.availabilityByProducts.find(
    (product) => product.productId === sku
  );
  return product;
};

export const getBopisStoreStockAvailabilility = (
  sku: string
): { isAvailable: boolean; quantity: number; pickUpTimeInDays?: number } => {
  const selectedStoreId = bopisState.storeSelection?.[0]?.location_id;
  if (!bopisState.cartProductsAvailability || !selectedStoreId)
    return { isAvailable: false, quantity: -1, pickUpTimeInDays: -1 };

  const product = checkProductAvailability(sku);
  if (!product) return { isAvailable: false, quantity: -1, pickUpTimeInDays: -1 };

  const allLocations = (product.availabilityByFulfillmentTypes || []).flatMap((fulfillment) =>
    (fulfillment.availabilityDetails || []).flatMap((detail) => detail.availabilityByLocations || [])
  );
  const location = allLocations.find((loc) => loc.location_id === selectedStoreId);
  return location
    ? {
        isAvailable: location.available_after_safety_stock > 0,
        quantity: location.available_after_safety_stock,
        pickUpTimeInDays: location.pickup_time_in_days,
      }
    : { isAvailable: false, quantity: -1, pickUpTimeInDays: -1 };
};

export const getPickupAvailabilityStatusText = (pickupTimeInDays: number, isOmniPdp?: boolean) => {
  switch (pickupTimeInDays) {
    case 0:
      return isOmniPdp ? 'Pickup today' : 'Available for pickup today';
    case 1:
      return isOmniPdp ? 'Pickup tomorrow' : 'Available for pickup tomorrow';
    case 3: {
      const bufferDays = getSTSBufferDays();
      const maxDays = pickupTimeInDays + bufferDays;
      return isOmniPdp
        ? `Pickup in ${pickupTimeInDays}${bufferDays > 0 ? `-${maxDays}` : ''} business days`
        : `Available for pickup in ${pickupTimeInDays}${bufferDays > 0 ? `-${maxDays}` : ''} business days`;
    }
    default:
      return isOmniPdp ? 'Pickup' : 'Available for pickup';
  }
};

export const getSTSBufferDays = (): number => {
  return SiteSettings?.stsBufferDays && SiteSettings?.stsBufferDays >= 0 ? SiteSettings?.stsBufferDays : 0;
};

export const transformPdpDataToBopisAnalyticsVariant = (
  variant: IVariant,
  product: IColorwayProductData
): BopisAnalyticsVariantType => {
  return {
    productId: product.id,
    variantId: variant.id,
    title: product.baseTitle,
    quantityAvailable: variant.quantityAvailable,
    sku: variant.sku,
    selectionOptions: toJS(variant.selectionOptions),
  };
};

export const transformCartDataToBopisAnalyticsVariant = (cartData: CartData): BopisAnalyticsVariantType[] =>
  cartData.regularItems.map((item) => ({
    productId: String(item.product_id),
    variantId: String(item.variant_id),
    title: item.product_title,
    quantityAvailable: item.quantityAvailable ?? '',
    sku: item.sku,
    selectionOptions: {
      Color: item.options_with_values[0]?.value || '',
      Size: item.options_with_values[1]?.value || '',
    },
  }));

export const getInStoreStoreFromAvailabilityDetailsByLocationId = (
  availabilityByLocations: IStoreAvailabilityData[] | null,
  locationId: string
) => {
  for (const location of availabilityByLocations || []) {
    if (location.location_id === locationId) {
      return location;
    }
  }

  return null;
};
